import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
    ShieldCheck, RefreshCw, Save, RotateCcw, Lock,
    ClipboardList, Shuffle, Cable, Trophy,
    UserMinus, Mail, MessageSquare,
    Users, AlertCircle, CheckSquare, Square,
} from "lucide-react";

// iter87.199.196 — Rebuilt around the CURRENT tile surface (see
// memory/CHANGELOG.md). Three groups: Tournament Admin, Rivalry Admin,
// Custom tournaments. Each togglable tile maps 1:1 to a real
// AdminPanel tile (see `PERMISSION_KEYS` in backend/co_admin.py).
// Locked rows (never grantable to a co-admin) render statically —
// no toggle, no key, primary-admin-only forever.
const GROUPS = [
    {
        key: "tournament",
        title: "Tournament Admin",
        desc: "Per-tournament tiles inside an AdminPanel.",
        toggles: [
            { key: "can_configure_scoring", label: "Scoring", desc: "Point values, teams-per-crewmate, teams-per-player.", icon: ClipboardList },
            { key: "can_run_draft", label: "Draft", desc: "Run / clear / re-run the draft. Destructive once-per-tournament action.", icon: Shuffle },
            { key: "can_manage_live_sync", label: "Live Sync", desc: "Status, coverage, manual resync for API-driven templates.", icon: Cable },
            { key: "can_configure_trophy_tokens", label: "Trophy Tokens", desc: "Config, opt-ins, confirm/unconfirm, finalize/unfinalize. Also covers Squares (iter87.554 — Squares now lives inside this tab).", icon: Trophy },
        ],
        locked: [
            { label: "Tournament Settings", desc: "Native to the primary rivalry admin — never available to co-admins." },
            { label: "Danger Zone", desc: "Locks / deletes the tournament — never available to co-admins." },
        ],
    },
    {
        key: "rivalry",
        title: "Rivalry Admin",
        desc: "League-wide tiles inside an AdminPanel.",
        toggles: [
            { key: "can_remove_participants", label: "Crewmates", desc: "Remove or rename other crewmates.", icon: UserMinus },
            { key: "can_invite_players", label: "Invites", desc: "Email invites + manual add-player.", icon: Mail },
            { key: "can_post_messages", label: "Messages", desc: "Rivalry Messages, broadcast email, Smack Stream toggle.", icon: MessageSquare },
        ],
        locked: [
            { label: "Rivalry Settings", desc: "Includes the Rivalry Danger Zone (self-archive) — never available to co-admins." },
            { label: "Co-Admins", desc: "Only a site admin can promote/demote co-admins — never available to co-admins." },
        ],
    },
    {
        key: "custom",
        title: "Custom tournaments",
        desc: "Teams / Schedule / Bracket Status / GotSport import — only exist on Custom and deprecated legacy-ESPN templates. Off by default.",
        toggles: [
            { key: "can_manage_custom_tiles", label: "Teams, Schedule & Bracket Status", desc: "Roster edits, manual match entry, GotSport import — Custom/legacy templates only.", icon: Users },
        ],
        locked: [],
    },
];

// Defaults must match CoAdminPermissions in backend/co_admin.py.
const DEFAULTS = {
    can_configure_scoring: true,
    can_run_draft: false,
    can_manage_live_sync: true,
    can_configure_trophy_tokens: true,
    can_remove_participants: true,
    can_invite_players: true,
    can_post_messages: true,
    can_manage_custom_tiles: false,
};

export default function SiteAdminCoAdminPermissions() {
    const [perms, setPerms] = useState(null);
    const [loading, setLoading] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await api.siteGetCoAdminPermissions();
            setPerms(data);
            setDirty(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load permissions");
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const toggle = (key) => {
        setPerms((prev) => ({ ...prev, [key]: !prev?.[key] }));
        setDirty(true);
    };

    // Group master toggle — simple bulk "select all / select none" on
    // that group's togglable keys (no separate persisted master flag).
    const toggleGroup = (group) => {
        const keys = group.toggles.map((t) => t.key);
        const allOn = keys.every((k) => perms[k]);
        const next = !allOn;
        setPerms((prev) => {
            const updated = { ...prev };
            keys.forEach((k) => { updated[k] = next; });
            return updated;
        });
        setDirty(true);
    };

    const save = async () => {
        setSaving(true);
        try {
            const next = await api.siteUpdateCoAdminPermissions(perms);
            setPerms(next);
            setDirty(false);
            toast.success("Permissions saved — applies to every co-admin across all rivalries");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        }
        setSaving(false);
    };

    const resetDefaults = () => {
        if (!window.confirm("Reset every co-admin permission toggle back to the safe defaults?")) return;
        setPerms({ ...DEFAULTS });
        setDirty(true);
    };

    if (loading || !perms) {
        return (
            <div className="brutal-card p-8 text-center" data-testid="coadmin-perms-loading">
                <RefreshCw className="w-6 h-6 mx-auto animate-spin text-zinc-500 mb-2" />
                <p className="text-sm text-zinc-500">Loading co-admin permissions…</p>
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="coadmin-perms-tab">
            {/* Header card */}
            <div className="brutal-card p-4 flex items-start gap-3 border-cyber/40">
                <div className="w-9 h-9 bg-cyber border-2 border-black shadow-brutal flex items-center justify-center shrink-0">
                    <ShieldCheck className="w-4 h-4 text-black" strokeWidth={2.5} />
                </div>
                <div className="min-w-0">
                    <p className="text-[10px] uppercase font-bold tracking-widest text-zinc-500">Co-admin role · universal toggles</p>
                    <p className="font-display font-extrabold text-lg leading-tight">Co-Admin Permissions</p>
                    <p className="text-[11px] text-zinc-400 leading-relaxed mt-1">
                        Toggle which AdminPanel tiles a co-admin can open. These settings apply
                        globally to <span className="text-cyber font-bold">every co-admin in every league</span>.
                        Primary rivalry admins always have full access regardless of these toggles.
                    </p>
                </div>
            </div>

            {/* Hard-locked notice */}
            <div className="brutal-card-light p-3 border-blaze/30">
                <p className="text-[11px] text-zinc-400 leading-relaxed flex items-start gap-2">
                    <AlertCircle className="w-3.5 h-3.5 text-blaze shrink-0 mt-0.5" />
                    <span>
                        <span className="text-blaze font-bold">Always primary admin only</span> — transferring
                        admin rights, accepting the rivalry's Terms & Conditions, Tournament Settings,
                        both Danger Zones, Rivalry Settings, and Co-Admins can <em>never</em> be granted to a co-admin.
                    </span>
                </p>
            </div>

            {/* Grouped toggle sections */}
            {GROUPS.map((group) => {
                const keys = group.toggles.map((t) => t.key);
                const allOn = keys.length > 0 && keys.every((k) => perms[k]);
                const MasterIcon = allOn ? CheckSquare : Square;
                return (
                    <div key={group.key} className="brutal-card p-4 space-y-3" data-testid={`coadmin-group-${group.key}`}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <p className="font-display font-extrabold text-base leading-tight">{group.title}</p>
                                <p className="text-[11px] text-zinc-500 leading-relaxed mt-0.5">{group.desc}</p>
                            </div>
                            {keys.length > 0 && (
                                <button
                                    type="button"
                                    onClick={() => toggleGroup(group)}
                                    data-testid={`coadmin-group-${group.key}-toggle-all`}
                                    className="brutal-btn-ghost text-[10px] py-1.5 px-2 shrink-0 whitespace-nowrap"
                                >
                                    <MasterIcon className="w-3.5 h-3.5" /> {allOn ? "Turn all off" : "Turn all on"}
                                </button>
                            )}
                        </div>

                        <div className="space-y-2">
                            {group.toggles.map((p) => {
                                const Icon = p.icon;
                                const on = !!perms[p.key];
                                return (
                                    <label
                                        key={p.key}
                                        data-testid={`coadmin-perm-${p.key}`}
                                        className={`brutal-card-light p-3 flex items-start gap-3 cursor-pointer transition-colors ${on ? "border-cyber/60" : "border-[#333]"}`}
                                    >
                                        <div className={`w-8 h-8 border-2 border-black shadow-brutal flex items-center justify-center shrink-0 ${on ? "bg-cyber" : "bg-zinc-800"}`}>
                                            <Icon className={`w-4 h-4 ${on ? "text-black" : "text-zinc-500"}`} strokeWidth={2.5} />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-bold leading-tight">{p.label}</p>
                                            <p className="text-[11px] text-zinc-400 leading-relaxed mt-0.5">{p.desc}</p>
                                        </div>
                                        <input
                                            type="checkbox"
                                            data-testid={`coadmin-perm-${p.key}-checkbox`}
                                            checked={on}
                                            onChange={() => toggle(p.key)}
                                            className="w-5 h-5 accent-cyber cursor-pointer mt-1 shrink-0"
                                        />
                                    </label>
                                );
                            })}
                            {group.locked.map((row) => (
                                <div
                                    key={row.label}
                                    data-testid={`coadmin-locked-${row.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                    className="p-3 flex items-start gap-3 opacity-70 border border-dashed border-zinc-700"
                                >
                                    <div className="w-8 h-8 border-2 border-zinc-700 flex items-center justify-center shrink-0">
                                        <Lock className="w-4 h-4 text-zinc-400" strokeWidth={2.5} />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-bold leading-tight text-zinc-300">{row.label}</p>
                                        <p className="text-[11px] text-zinc-400 leading-relaxed mt-0.5">{row.desc}</p>
                                    </div>
                                    <span className="text-[9px] uppercase font-bold text-zinc-400 tracking-widest shrink-0 mt-1.5">Locked</span>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })}

            {/* Action bar */}
            <div className="brutal-card p-3 flex flex-wrap items-center justify-between gap-3 border-cyber/40">
                <p className="text-[11px] text-zinc-400">
                    {dirty ? <span className="text-volt font-bold">Unsaved changes</span> : "No unsaved changes"}
                </p>
                <div className="flex gap-2 flex-wrap">
                    <button
                        data-testid="coadmin-perms-reset-btn"
                        onClick={resetDefaults}
                        disabled={saving}
                        className="brutal-btn-ghost text-xs py-2 disabled:opacity-60"
                    >
                        <RotateCcw className="w-3.5 h-3.5" /> Reset defaults
                    </button>
                    <button
                        data-testid="coadmin-perms-save-btn"
                        onClick={save}
                        disabled={!dirty || saving}
                        className="brutal-btn text-xs py-2 bg-cyber text-black border-black shadow-brutalYellow disabled:opacity-50"
                    >
                        <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save changes"}
                    </button>
                </div>
            </div>
        </div>
    );
}
